import CheckCircle from "@mui/icons-material/CheckCircle";
import HourglassTop from "@mui/icons-material/HourglassTop";
import PersonAdd from "@mui/icons-material/PersonAdd";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { RejectionRoutingDialog } from "components/Submission/RejectionRoutingDialog";
import { ReviewerManagementDialog } from "components/Submission/ReviewerManagementDialog";
import { Reviewer } from "components/Submission/ReviewersEditList";
import { ReviewersDatagrid } from "components/Submission/ReviewersList";
import { SubmissionFormProvider } from "contexts/SubmissionFormContext";
import { isActiveReviewer } from "encamp-shared/src/submissions/activeReviewer";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  ReviewerStatus,
  ReviewMode,
  SubmissionEncampStatus,
  SubmissionReviewerInput,
  SubmissionStatus,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { SubmissionFormData } from "hooks/useSubmissionForm";
import {
  useApproveSubmissionReview,
  useAssignPeerReviewToMe,
  useMoveToReview,
  useRejectSubmissionReview,
  useUpdateSubmission,
} from "hooks/useSubmissionMutations";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { parseDateLikeToLocalDateTime, toUtcMidnightIsoFromDateLike } from "util/dates";

import { StepHeader } from "../StepHeader";

type StepperReviewer = NonNullable<
  ReturnType<typeof useWasteStepper>["submission"]
>["reviewers"][number];

/** "Name (email)" when we have a person record, otherwise just the email. */
function reviewerName(reviewer: StepperReviewer): string {
  const { person, email } = reviewer.user;
  return person ? `${getFullName(person)} (${email})` : email;
}

/** Map a persisted submission reviewer to the reviewer-management UI shape. */
function toUiReviewer(r: StepperReviewer): Reviewer {
  return {
    id: r.user.id,
    submissionReviewerId: r.id,
    name: r.user.person ? getFullName(r.user.person) : r.user.email,
    email: r.user.email,
    // Stored as UTC midnight; parse by literal calendar day so the picker
    // doesn't roll the date back a day in negative-offset zones.
    dueDate: r.dueDate ? parseDateLikeToLocalDateTime(r.dueDate) : undefined,
    isSigner: r.isSigner,
    isStaff: r.isStaff,
    order: r.reviewOrder,
    reviewerStatus: r.reviewerStatus ?? null,
  };
}

/**
 * Review stepper step — the single home for the review workflow.
 *
 * Staff add/manage the submission's reviewers (sequential/parallel mode, the
 * add-reviewer dialog, and the reviewers datagrid). "Include reviewers" is the
 * default; opting out ("No review required") demands an explicit confirmation
 * since it skips reviewer sign-off entirely. With reviewers, "Send for Review"
 * persists the reviewer config and sends the submission into the review workflow
 * (`moveToReview`). Without review, "Submit for Processing" persists the opt-out
 * and — with no reviewers to wait on — the backend moves the submission straight
 * to PROCESSING_SUBMISSION, so the button confirms before sending.
 *
 * Peer review is an open Encamp-staff queue, chosen in the add-reviewer dialog
 * (the "Encamp Staff (peer review)" option) alongside individual reviewers —
 * GitHub-style. It sets `peerReviewRequired`, which routes the submission through
 * ENCAMP_INITIAL_REVIEW; any staff member can then claim it via "Assign peer
 * review to me" (`assignPeerReviewToMe`). All reviewer-adding — the staff queue
 * and named customers — happens in that one dialog.
 *
 * Reviewers (staff peers or customers) approve or reject here: the active
 * reviewer gets an approve/reject radio panel, and non-staff see a read-only
 * progress list (staff read progress from the datagrid). This step is terminal:
 * the final approval moves the submission to PROCESSING_SUBMISSION (freezing the
 * snapshot), so whoever completes the last outstanding review — the reviewer
 * themselves or a staff override-approve in the datagrid — gets a confirm
 * dialog warning that completing the review submits for processing.
 *
 * The signer is intentionally out of scope here, but any existing signer is
 * preserved on save so saving reviewers never strands a configured signer.
 */
export function ReviewStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { isStaff, user } = useCurrentUser();
  const { submission, canEdit, isFrozen } = useWasteStepper();
  const [moveToReview, { loading }] = useMoveToReview();
  const [updateSubmission, { loading: savingOptOut }] = useUpdateSubmission();
  const [approveReview, { loading: approving }] = useApproveSubmissionReview();
  const [rejectReview, { loading: rejecting }] = useRejectSubmissionReview();
  const [assignPeerReviewToMe, { loading: claiming }] = useAssignPeerReviewToMe();
  const { touchStep } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep } = useStepperNavigation();

  const disabled = !canEdit || isFrozen;

  const persistedReviewers = useMemo(() => submission?.reviewers ?? [], [submission]);
  const status = submission?.status ?? SubmissionStatus.InProgress;

  // Persisted non-signer reviewers drive the progress list and active-reviewer
  // logic (the local `reviewers` state below is the editable config shape).
  const nonSignerReviewers = useMemo(
    () => persistedReviewers.filter((r) => !r.isSigner),
    [persistedReviewers]
  );
  const myReviewer = useMemo(
    () => nonSignerReviewers.find((r) => r.user.id === user?.id),
    [nonSignerReviewers, user?.id]
  );
  const isMyTurn = useMemo(
    () =>
      !!user?.id &&
      isActiveReviewer(user.id, {
        status,
        reviewMode: submission?.reviewMode ?? "PARALLEL",
        reviewers: nonSignerReviewers.map((r) => ({
          userId: r.user.id,
          // Phased by the user's type (not the row's isStaff column) so a
          // staff user added through the regular picker still gets their turn
          // during ENCAMP_INITIAL_REVIEW — matching the backend routing.
          userType: r.user.type,
          isSigner: r.isSigner,
          reviewOrder: r.reviewOrder,
          reviewerStatus: r.reviewerStatus ?? null,
        })),
      }),
    [nonSignerReviewers, status, submission?.reviewMode, user?.id]
  );

  // A customer reviewer who is in the review queue but whose turn hasn't come
  // up yet — the submission is still in the staff phase (ENCAMP_INITIAL_REVIEW)
  // or an earlier reviewer in a sequential customer cohort hasn't acted. Mirrors
  // the document-review flow's "waiting for turn" banner so a customer isn't
  // left staring at a read-only progress list with no explanation.
  const myReviewerStatus = myReviewer?.reviewerStatus ?? null;
  const isWaitingForTurn =
    !isStaff &&
    !!myReviewer &&
    !isMyTurn &&
    myReviewerStatus !== ReviewerStatus.Approved &&
    myReviewerStatus !== ReviewerStatus.Rejected &&
    (status === SubmissionStatus.EncampInitialReview || status === SubmissionStatus.InReview);

  // Active reviewer's choice: true = approve (default), false = needs edits.
  const [rejectionComment, setRejectionComment] = useState("");
  const [reviewFinished, setReviewFinished] = useState(true);

  // Whether the viewer's approval would be the last one outstanding — every
  // other (non-signer) reviewer has already approved — in which case approving
  // moves the submission into processing and the Submit click confirms first.
  const isFinalApproval = useMemo(
    () =>
      !!myReviewer &&
      nonSignerReviewers
        .filter((r) => r.id !== myReviewer.id)
        .every((r) => r.reviewerStatus === ReviewerStatus.Approved),
    [myReviewer, nonSignerReviewers]
  );

  const [reviewers, setReviewers] = useState<Reviewer[]>(() =>
    persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer)
  );

  // Re-sync the editable reviewer list whenever a refetch lands so the datagrid
  // reflects review actions (approve/reject) without a page reload. Local-only
  // edits exist solely pre-review, when nothing else refetches the stepper, so
  // this never clobbers unsaved changes.
  useEffect(() => {
    setReviewers(persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer));
  }, [persistedReviewers]);
  // "Include reviewers" is the default; `false` only ever means the staff user
  // explicitly confirmed the no-review path (stepper creation persists `true`).
  const [reviewRequired, setReviewRequired] = useState<boolean>(submission?.reviewRequired ?? true);
  // Open-queue peer review: any Encamp staff can review. Set from the
  // add-reviewer dialog's "Encamp Staff (peer review)" option, persisted directly.
  const [peerReviewRequired, setPeerReviewRequired] = useState<boolean>(
    submission?.peerReviewRequired ?? false
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [noReviewConfirmOpen, setNoReviewConfirmOpen] = useState(false);
  const [rejectionRoutingOpen, setRejectionRoutingOpen] = useState(false);
  // Completing the last outstanding review is terminal (moves the submission
  // into processing), so both paths that can do it confirm first: the active
  // reviewer approving their own review ("self"), and a staff override-approve
  // of the final pending reviewer in the datagrid ("override", which carries
  // the not-yet-applied reviewer list). The no-review "Submit for Processing"
  // path has its own dialog below.
  const [finalApprovalConfirm, setFinalApprovalConfirm] = useState<
    { type: "self" } | { type: "override"; next: Reviewer[] } | null
  >(null);
  const [submitWithoutReviewConfirmOpen, setSubmitWithoutReviewConfirmOpen] = useState(false);

  // Staff resolve a reviewer's rejection here via the same rejection-routing
  // dialog as the legacy submission form (Send to Assignee / Push Forward /
  // Send Back to Reviewer). Resetting the rejecting reviewer directly is
  // intentionally blocked, so this is the recovery path.
  const isRejected = isStaff && submission?.encampStatus === SubmissionEncampStatus.Rejected;

  // The legacy reviewers datagrid drives review mode through a typed
  // react-hook-form `setValue`; host a local form solely to satisfy that
  // contract and read the current mode back via `watch`.
  const { setValue, watch } = useForm<SubmissionFormData>({
    defaultValues: { reviewMode: submission?.reviewMode ?? ReviewMode.Parallel },
  });
  const reviewMode = watch("reviewMode") ?? ReviewMode.Parallel;

  // Builds the reviewer mutation input from a reviewer list, preserving any
  // existing signer so persisting reviewers never strands it, and carrying each
  // reviewer's `reviewerStatus` (so a staff override-approve sticks).
  const buildReviewerInputs = useCallback(
    (list: Reviewer[]): SubmissionReviewerInput[] => {
      const signerInputs: SubmissionReviewerInput[] = persistedReviewers
        .filter((r) => r.isSigner)
        .map((r) => ({
          userId: r.user.id,
          reviewOrder: r.reviewOrder,
          isStaff: r.isStaff,
          isRequired: r.isRequired,
          isSigner: true,
          dueDate: r.dueDate ?? undefined,
          signerNameOverride: r.signerNameOverride ?? undefined,
          signerEmailOverride: r.signerEmailOverride ?? undefined,
        }));
      const reviewerInputs: SubmissionReviewerInput[] = reviewRequired
        ? list.map((r) => ({
            userId: r.id,
            reviewOrder: r.order,
            isStaff: r.isStaff,
            isRequired: true,
            isSigner: false,
            // Serialize as UTC midnight (not raw `.toISO()`, which keeps the
            // local offset and shifts the calendar day in positive-offset
            // zones) so the stored date round-trips.
            dueDate: r.dueDate ? (toUtcMidnightIsoFromDateLike(r.dueDate) ?? undefined) : undefined,
            reviewerStatus: r.reviewerStatus ?? undefined,
          }))
        : [];
      return [...reviewerInputs, ...signerInputs];
    },
    [persistedReviewers, reviewRequired]
  );

  const handleSendForReview = async () => {
    try {
      // moveToReview saves the reviewer config (via its UpdateSubmissionInput)
      // AND sends the submission into the review workflow in one call.
      const result = await moveToReview({
        variables: {
          input: {
            id: submissionId,
            reviewRequired,
            peerReviewRequired,
            reviewMode,
            reviewers: buildReviewerInputs(reviewers),
          },
        },
      });
      if (result.data?.moveToReview) {
        // Sending for review starts the step, it doesn't finish it — the step
        // is done only once every reviewer has approved (at which point the
        // submission freezes and the display derives Done from its status).
        await touchStep(SubmissionStepType.Review, SubmissionStepStatus.InProgress);
        alerts.success("Sent for review.");
        goToNextStep(SubmissionStepType.Review);
      }
    } catch (error) {
      alerts.error("Failed to send for review.", error);
    }
  };

  // No-review path: persist the opt-out and submit. With no reviewers to wait
  // on, the backend's startReviewWorkflow moves the submission straight to
  // PROCESSING_SUBMISSION — this IS the terminal action, so the button opens a
  // confirm dialog before calling this.
  const handleSubmitWithoutReview = async () => {
    try {
      const result = await moveToReview({
        variables: {
          input: {
            id: submissionId,
            reviewRequired: false,
            peerReviewRequired: false,
            reviewMode,
            // reviewRequired is false here, so this resolves to signers only.
            reviewers: buildReviewerInputs([]),
          },
        },
      });
      if (result.data?.moveToReview) {
        await touchStep(SubmissionStepType.Review, SubmissionStepStatus.Completed);
        alerts.success("Submitted for processing.");
        goToNextStep(SubmissionStepType.Review);
      }
    } catch (error) {
      alerts.error("Failed to submit for processing.", error);
    }
  };

  // Persists a datagrid status override (staff approve/reset) via
  // `updateSubmission` so the approval sticks. Applied either directly from
  // `handleReviewersChange` or from the final-approval confirm dialog.
  const persistReviewerOverride = useCallback(
    async (next: Reviewer[]) => {
      setReviewers(next);
      try {
        await updateSubmission({
          variables: {
            input: {
              id: submissionId,
              reviewRequired,
              peerReviewRequired: reviewRequired && peerReviewRequired,
              reviewMode,
              reviewers: buildReviewerInputs(next),
            },
          },
        });
        alerts.success("Reviewer approval saved.");
      } catch (error) {
        alerts.error("Failed to save the reviewer approval.", error);
      }
    },
    [
      updateSubmission,
      submissionId,
      reviewRequired,
      peerReviewRequired,
      reviewMode,
      buildReviewerInputs,
      alerts,
    ]
  );

  // The datagrid mutates `reviewers` locally for adds/reorders/deletes (persisted
  // on "Send for Review") and for the staff approve/reset override, which
  // persists immediately. When the override would approve the final pending
  // reviewer, the backend advances the submission into processing — so that case
  // confirms first (the local state is left untouched until confirmed).
  const handleReviewersChange = useCallback(
    (next: Reviewer[]) => {
      const statusChanged = next.some((r) => {
        const prev = reviewers.find((p) => p.id === r.id);
        return prev != null && prev.reviewerStatus !== r.reviewerStatus;
      });
      if (!statusChanged) {
        setReviewers(next);
        return;
      }
      const completesAllApprovals =
        next.length > 0 && next.every((r) => r.reviewerStatus === ReviewerStatus.Approved);
      if (completesAllApprovals) {
        setFinalApprovalConfirm({ type: "override", next });
        return;
      }
      void persistReviewerOverride(next);
    },
    [reviewers, persistReviewerOverride]
  );

  const handleApprove = useCallback(async () => {
    if (!myReviewer) return;
    try {
      await approveReview({ variables: { submissionReviewerId: myReviewer.id } });
      alerts.success("Review approved.");
    } catch (error) {
      alerts.error("Failed to approve the review.", error);
    }
  }, [approveReview, myReviewer, alerts]);

  const handleReject = useCallback(async () => {
    if (!myReviewer) return;
    try {
      await rejectReview({
        variables: {
          submissionReviewerId: myReviewer.id,
          rejectionComment: rejectionComment.trim() || undefined,
        },
      });
      alerts.success("Review rejected and sent back for changes.");
      setRejectionComment("");
    } catch (error) {
      alerts.error("Failed to reject the review.", error);
    }
  }, [rejectReview, myReviewer, rejectionComment, alerts]);

  const handleClaimPeerReview = useCallback(async () => {
    try {
      await assignPeerReviewToMe({ variables: { submissionId } });
      alerts.success("You're now assigned as the peer reviewer.");
    } catch (error) {
      alerts.error("Failed to assign peer review to you.", error);
    }
  }, [assignPeerReviewToMe, submissionId, alerts]);

  // Open peer-review queue: any staff member can claim a submission waiting in
  // the Encamp initial-review phase, as long as they aren't already a reviewer.
  const canClaimPeerReview =
    isStaff &&
    submission?.peerReviewRequired === true &&
    status === SubmissionStatus.EncampInitialReview &&
    !myReviewer;

  // Non-staff reviewers must give a reason; staff may reject without one.
  const canReject = isStaff || rejectionComment.trim().length > 0;

  // Customers never manage reviewers, and staff can't once the submission is in
  // review — so only pre-review staff get the "add reviewers" framing.
  let stepDescription: string;
  if (!isStaff) {
    stepDescription =
      "Review this 8700-12 notification and approve it, or send it back with requested changes, before it is submitted.";
  } else if (disabled) {
    stepDescription =
      "Track each reviewer's sign-off on this 8700-12 notification. You can force-complete an individual reviewer if needed.";
  } else {
    stepDescription =
      "Add the reviewers who must sign off on this 8700-12 notification before it is submitted, or confirm that no review is required.";
  }

  return (
    <SubmissionFormProvider issues={[]}>
      <Stack spacing={2}>
        <StepHeader title="Review" description={stepDescription} />

        {isWaitingForTurn && (
          <Alert severity="warning" icon={<HourglassTop />} sx={{ borderRadius: "4px" }}>
            <Typography fontWeight="bold" marginBottom={0.5}>
              Submission not ready for review
            </Typography>
            <Typography variant="body2">
              This submission is being worked on by Encamp Staff and the reviewer(s) and signer will
              be notified when it is ready for review.
            </Typography>
          </Alert>
        )}

        {isRejected && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => setRejectionRoutingOpen(true)}>
                Resolve
              </Button>
            }
          >
            This submission has been <strong>rejected</strong> by a reviewer. Use rejection routing
            to determine next steps.
          </Alert>
        )}

        {canClaimPeerReview && (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                loading={claiming}
                onClick={handleClaimPeerReview}
              >
                Assign to me
              </Button>
            }
          >
            This submission is open for Encamp staff peer review. Assign it to yourself to review
            and approve it.
          </Alert>
        )}

        {isStaff && (
          <Stack spacing={2}>
            {/* Add-reviewer affordance is a top-right icon, matching the
                document-review submission form. The grid's built-in button
                only appears while the list is empty. */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="medium">
                Reviewer(s)
              </Typography>
              <Tooltip title="Add reviewer">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Add reviewer"
                    onClick={() => setDialogOpen(true)}
                    disabled={disabled || !reviewRequired}
                    sx={{ color: !disabled && reviewRequired ? "primary.main" : "inherit" }}
                  >
                    <PersonAdd fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <RadioGroup
              // FormGroup defaults to flex-wrap: wrap, and a WRAPPED column
              // flex container sizes its line to the widest child's content
              // instead of the container width — the nested datagrid kept its
              // stale pre-comments-panel width and bled over the panel.
              // nowrap forces the line (and the grid) to the container width.
              sx={{ flexWrap: "nowrap" }}
              value={reviewRequired ? "true" : "false"}
              onChange={(e) => {
                if (disabled) return;
                if (e.target.value === "true") {
                  setReviewRequired(true);
                  return;
                }
                // Opting out of review is deliberate: confirm before flipping.
                setNoReviewConfirmOpen(true);
              }}
            >
              <FormControlLabel
                value="true"
                disabled={disabled}
                control={<Radio size="small" disabled={disabled} />}
                label={<Typography variant="body2">Include reviewers</Typography>}
              />

              {reviewRequired && (
                // minWidth: 0 lets the datagrid shrink with the column when
                // the comments panel takes width (see RadioGroup nowrap note).
                // Add reviewers — named customers or the Encamp staff peer
                // review queue — from the add-reviewer dialog opened by the
                // grid's add action.
                <Box sx={{ my: 1, minWidth: 0 }}>
                  {peerReviewRequired && (
                    <Chip
                      label="Encamp Staff · Peer review"
                      color="info"
                      variant="outlined"
                      size="small"
                      onDelete={disabled ? undefined : () => setPeerReviewRequired(false)}
                      sx={{ mb: 1 }}
                    />
                  )}
                  <ReviewersDatagrid
                    reviewers={reviewers}
                    setReviewers={disabled ? undefined : handleReviewersChange}
                    reviewMode={reviewMode}
                    setValue={disabled ? undefined : setValue}
                    handleAddReviewer={() => setDialogOpen(true)}
                    submissionDueDate={
                      submission?.dueDate
                        ? parseDateLikeToLocalDateTime(submission.dueDate)
                        : undefined
                    }
                    disabled={disabled}
                    isStaff={isStaff}
                    submissionId={submissionId}
                    submissionEncampStatus={submission?.encampStatus ?? null}
                    activeReviewerId={isMyTurn ? myReviewer?.id : undefined}
                  />
                </Box>
              )}

              <FormControlLabel
                value="false"
                disabled={disabled}
                control={<Radio size="small" disabled={disabled} />}
                label={
                  <Typography variant="body2" color="text.secondary">
                    No review required
                  </Typography>
                }
              />
            </RadioGroup>

            {!reviewRequired && (
              <Alert severity="info">
                No reviewer sign-off will be collected — this 8700-12 notification will be submitted
                for processing.
              </Alert>
            )}

            {/* Sits with the reviewer configuration (rather than floating at
                  the page bottom), and demotes to outlined while the viewer is the
                  active reviewer — their "Your review" panel's Submit button
                  is the page's one contained primary in that state, so the
                  two CTAs never compete. "Include reviewers" with an empty
                  list is a contradictory state, so progression stays blocked
                  until a reviewer is added (or the no-review opt-out is
                  explicitly confirmed). */}
            {!disabled && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant={isMyTurn && myReviewer ? "outlined" : "contained"}
                  loading={loading || savingOptOut}
                  // Peer review with no named customer reviewers is a valid
                  // send (the open staff queue handles it), so block only when
                  // there's neither a named reviewer nor peer review.
                  disabled={reviewRequired && reviewers.length === 0 && !peerReviewRequired}
                  onClick={
                    reviewRequired
                      ? handleSendForReview
                      : () => setSubmitWithoutReviewConfirmOpen(true)
                  }
                >
                  {reviewRequired ? "Send for Review" : "Submit for Processing"}
                </Button>
              </Box>
            )}
          </Stack>
        )}

        {/* Read-only progress for non-staff reviewers, who don't get the staff
            datagrid's status columns. Staff read progress from the datagrid. */}
        {!isStaff && nonSignerReviewers.length > 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Reviewers
            </Typography>
            <Stack spacing={1}>
              {nonSignerReviewers.map((reviewer) => {
                const approved = reviewer.reviewerStatus === ReviewerStatus.Approved;
                const rejected = reviewer.reviewerStatus === ReviewerStatus.Rejected;
                // Every reviewer (including the viewer) shows by name — like a
                // GitHub reviewers list — rather than "You". Their pending chip
                // still flips to "Your turn" once they're the active reviewer.
                const isCurrentUser = reviewer.user.id === user?.id;
                let chipLabel = "Pending";
                // Design-system chips: semantic palette + outlined (light-on-the-
                // eyes), never the default solid fill. The neutral/highlight role
                // uses `info` per the spec's role shift (no `primary`).
                let chipColor: "success" | "error" | "info" | "default" = "default";
                if (approved) {
                  chipLabel = "Approved";
                  chipColor = "success";
                } else if (rejected) {
                  chipLabel = "Rejected";
                  chipColor = "error";
                } else if (isCurrentUser && isMyTurn) {
                  chipLabel = "Your turn";
                  chipColor = "info";
                }
                return (
                  <Box
                    key={reviewer.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {approved ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <RadioButtonUnchecked color="disabled" fontSize="small" />
                      )}
                      <Typography variant="body2" fontWeight={isCurrentUser ? "medium" : undefined}>
                        {reviewerName(reviewer)}
                      </Typography>
                    </Box>
                    <Chip size="small" label={chipLabel} color={chipColor} variant="outlined" />
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {isMyTurn && myReviewer && (
          <Box>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Your review
            </Typography>
            <Stack spacing={2}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  Approve this 8700-12 notification, or send it back with a reason.
                </FormLabel>
                <RadioGroup
                  value={reviewFinished ? "true" : "false"}
                  onChange={(e) => setReviewFinished(e.target.value === "true")}
                >
                  {/* Emoji prefixes mirror the document-review reviewer UI
                      (SubmissionDisplayReviewOrSignDocument): ✅ Approve / 🔄 Needs edits. */}
                  <FormControlLabel value="true" control={<Radio />} label="✅ Approve" />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label={`🔄 Needs edits${isStaff ? "" : " (reason required)"}`}
                  />
                </RadioGroup>
              </FormControl>
              {!reviewFinished && (
                <TextField
                  label={isStaff ? "Reason for changes (optional)" : "Reason for changes"}
                  value={rejectionComment}
                  onChange={(e) => setRejectionComment(e.target.value)}
                  placeholder="Describe what needs to be corrected before resubmission."
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                {/* Stays primary even for "Needs edits" — a red CTA makes
                    reviewers hesitant to reject when rejection is the correct
                    path; the selected radio communicates the outcome. When the
                    viewer's approval is the last one outstanding it is the
                    terminal action (submits for processing), so it confirms
                    first. */}
                <Button
                  variant="contained"
                  disabled={!reviewFinished && !canReject}
                  loading={approving || rejecting}
                  onClick={() => {
                    if (!reviewFinished) {
                      void handleReject();
                      return;
                    }
                    if (isFinalApproval) {
                      setFinalApprovalConfirm({ type: "self" });
                      return;
                    }
                    void handleApprove();
                  }}
                >
                  Submit review
                </Button>
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>

      <ConfirmDialog
        open={noReviewConfirmOpen}
        onClose={() => setNoReviewConfirmOpen(false)}
        onConfirm={() => {
          setReviewRequired(false);
          setNoReviewConfirmOpen(false);
        }}
        header="Proceed without review?"
        info="No reviewer sign-off will be collected for this 8700-12 notification. Are you sure no review is required?"
        confirmText="Proceed without review"
        cancelText="Cancel"
      />

      {/* Terminal action for the no-review path: with no reviewers to wait on,
          submitting moves the notification straight into processing. */}
      <ConfirmDialog
        open={submitWithoutReviewConfirmOpen}
        loading={loading}
        onClose={() => setSubmitWithoutReviewConfirmOpen(false)}
        onConfirm={async () => {
          await handleSubmitWithoutReview();
          setSubmitWithoutReviewConfirmOpen(false);
        }}
        header="Submit for processing?"
        info="No review is required, so this 8700-12 notification will be submitted for processing now. This can't be undone."
        confirmText="Submit for Processing"
        cancelText="Cancel"
      />

      {/* Completing the last outstanding review moves the submission into
          processing (freezing the snapshot) — warn whoever is about to do it:
          the active reviewer approving their own review, or a staff user
          override-approving the final pending reviewer in the datagrid. */}
      <ConfirmDialog
        open={finalApprovalConfirm !== null}
        loading={approving || savingOptOut}
        onClose={() => setFinalApprovalConfirm(null)}
        onConfirm={async () => {
          if (finalApprovalConfirm?.type === "override") {
            await persistReviewerOverride(finalApprovalConfirm.next);
          } else {
            await handleApprove();
          }
          setFinalApprovalConfirm(null);
        }}
        header="Complete review and submit?"
        info={
          finalApprovalConfirm?.type === "override"
            ? "This approval completes the review — the 8700-12 notification will be submitted for processing. This can't be undone."
            : "You are the last reviewer. Completing your review will submit this 8700-12 notification for processing. This can't be undone."
        }
        confirmText="Approve & Submit"
        cancelText="Cancel"
      />

      {dialogOpen && (
        <ReviewerManagementDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          initialReviewers={reviewers}
          initialReviewMode={reviewMode}
          // Stepper-only: offer the open Encamp-staff peer-review queue instead
          // of a named staff reviewer (the legacy submission form keeps the named
          // picker).
          showEncampStaffPeerReview
          initialPeerReviewRequired={peerReviewRequired}
          submissionFacilityIds={[facilityId]}
          onSave={(nextReviewers, nextReviewMode, nextPeerReviewRequired) => {
            setReviewers(nextReviewers);
            setValue("reviewMode", nextReviewMode, { shouldDirty: true });
            setPeerReviewRequired(nextPeerReviewRequired ?? false);
            if ((nextReviewers.length > 0 || nextPeerReviewRequired) && !reviewRequired) {
              setReviewRequired(true);
            }
            setDialogOpen(false);
          }}
        />
      )}

      {rejectionRoutingOpen && (
        <RejectionRoutingDialog
          open={rejectionRoutingOpen}
          onClose={() => setRejectionRoutingOpen(false)}
          submissionId={submissionId}
          additionalRefetchQueries={["GetSubmissionStepper"]}
        />
      )}
    </SubmissionFormProvider>
  );
}
